import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { AppRole } from "@/lib/types";
import { getErrorMessage } from "@/lib/utils";
import { toast } from "sonner";

export interface UserWithRoles {
  id: string;
  user_id: string;
  name: string;
  position?: string;
  email: string;
  avatar_url?: string;
  is_active: boolean;
  created_at?: string;
  roles: AppRole[];
}

export function useUsers() {
  const queryClient = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await apiClient.getUsers();
      const list = (response ?? []) as Array<Record<string, unknown> & { role?: AppRole; roles?: AppRole[] }>;
      return list.map((u) => ({
        ...u,
        roles: Array.isArray(u.roles) ? u.roles : u.role != null ? [u.role] : [],
      })) as unknown as UserWithRoles[];
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (userData: {
      email: string;
      name: string;
      position?: string;
      role: AppRole;
    }) => {
      const response = await apiClient.createUser(userData);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User created successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to create user"));
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<UserWithRoles>;
    }) => {
      const response = await apiClient.updateUser(id, updates as any);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User updated successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to update user"));
    },
  });

  const updateUserStatus = async (userId: string, isActive: boolean) => {
    try {
      await apiClient.toggleUserStatus(userId);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User status updated");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update user status"));
    }
  };

  const deleteUserMutation = useMutation({
    mutationFn: async (id: string) => apiClient.deleteUser(id),
    onSuccess: (data) => {
      if (data?.success) {
        queryClient.invalidateQueries({ queryKey: ["users"] });
        queryClient.invalidateQueries({ queryKey: ["tickets"] });
        toast.success("User deleted successfully");
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to delete user"));
    },
  });

  const reassignUserTicketsMutation = useMutation({
    mutationFn: async (body: { fromUserId: string; toUserId: string; ticketsIds: string[] }) => {
      await apiClient.reassignUserTickets(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Tickets reassigned and user deleted successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to reassign tickets"));
    },
  });

  return {
    users: usersQuery.data ?? [],
    isLoading: usersQuery.isLoading,
    error: usersQuery.error,
    createUser: createUserMutation,
    updateUser: updateUserMutation,
    updateUserStatus,
    deleteUser: deleteUserMutation,
    reassignUserTickets: reassignUserTicketsMutation,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  };
}

export function useTicketAssignees(ticketId: string) {
  const queryClient = useQueryClient();

  const assigneesQuery = useQuery({
    queryKey: ["ticket-assignees", ticketId],
    queryFn: async () => {
      if (!ticketId) return [];
      // TODO: Implement get ticket assignees API endpoint
      return [];
    },
    enabled: !!ticketId,
  });

  const addAssigneeMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!ticketId) throw new Error("No ticket ID");
      // TODO: Implement assign user to ticket API endpoint
      return { ticketId, userId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["ticket-assignees", ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("User assigned to ticket");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to assign user"));
    },
  });

  const removeAssigneeMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!ticketId) throw new Error("No ticket ID");
      // TODO: Implement unassign user from ticket API endpoint
      return { ticketId, userId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["ticket-assignees", ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("User unassigned from ticket");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to unassign user"));
    },
  });

  return {
    assignees: assigneesQuery.data ?? [],
    isLoading: assigneesQuery.isLoading,
    error: assigneesQuery.error,
    addAssignee: addAssigneeMutation,
    removeAssignee: removeAssigneeMutation,
  };
}

export function useAssignees() {
  return useQuery({
    queryKey: ["assignees"],
    queryFn: async () => {
      // TODO: Implement get all assignable users API endpoint
      return [] as UserWithRoles[];
    },
  });
}
